import {StorageService} from '../storage/storage.service';
import {Injectable} from '@angular/core';
import {Store} from '@ngxs/store';
import {firstValueFrom} from 'rxjs';
import {cloneDeep} from 'lodash';
import {ScheduleAppointmentRequestDTO} from 'src/app/dtos/service/schedule-appointment-request.dto';
import {ActivatedRoute, Router} from '@angular/router';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {LocalStorageEnum} from '../../enums/utils';
import {IUser} from '../../models/auth';
import {UserService} from '../user/user.service';
import {AppointmentActions, AppointmentState, AppointmentStatusEnum, IAppointment} from '../../models/appointment';
import {ServiceAction} from '../../models/service';

@Injectable({
  providedIn: 'root'
})
export class AppointmentServiceService {
  confirmModal?: NzModalRef;

  constructor(
    private store: Store,
    private activatedRouter: ActivatedRoute,
    private modal: NzModalService,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService,
  ) { }

  /**
   * Verifica si la cita está completada o no
   * @param appointmentId string del id la cita
   */
  async isAppointmentCompleted(appointmentId: string): Promise<boolean> {
    await firstValueFrom((this.store.dispatch(new AppointmentActions.GetById(appointmentId))));
    const appointment: IAppointment = cloneDeep(
      await firstValueFrom((this.store.selectOnce(AppointmentState.getAppointmentSelected)))
    );
    return appointment.status === AppointmentStatusEnum.COMPLETED || appointment.status === AppointmentStatusEnum.CANCELLED;
  }

  /**
   * Da por terminada la cita dado el id
   */
  async completeCurrentAppointment(appointmentId: string): Promise<void> {
    try {
      // obtener de LS el valor actual del tiempo de la consulta
      const duration: number = this.storageService.getLocalItem('durationAppointment') || 0;
      await firstValueFrom((this.store.dispatch(new ServiceAction.CompleteAppointment(appointmentId, duration))));
    } catch (error) {
      throw new Error(error);
    }
  }

  /**
   * Envía petición al servidor para iniciar una nueva cita
   */
  async startNewAppointmentNow(
    appointment: ScheduleAppointmentRequestDTO,
    clinicId: string,
    patientId?: string,
    redirectToNewAppointment = false
  ): Promise<void> {
    if (!appointment) {
      throw new Error('Ha ocurrido un error al obtener la información');
    }
    try {
      await firstValueFrom((this.store.dispatch(new AppointmentActions.CreateRightNow(appointment, true))));
      // Redigir a su respectiva página
      const newAppointment = (await firstValueFrom(this.store.selectOnce(AppointmentState.getAppointmentSelected)));
      if (!newAppointment) {
        throw new Error('Ha ocurrido un error al obtener la información');
      }
      if (redirectToNewAppointment) {
        await this.navigateToConsultation(newAppointment._id, clinicId, patientId);
      }
    } catch (error) {
      throw new Error(error);
    }
  }

  /**
   *  Redirigir a la vista de la cita dada (id)
   */
  async navigateToConsultation(
    appointmentId: string,
    clinicId: string,
    patientId: string
  ): Promise<void> {
    if (!patientId) return;
    const {user} = this.storageService.getLocalItem<{user: IUser, token: string}>(LocalStorageEnum.USER);
    await this.router.navigate([
      'clinic',
      clinicId,
      'patients',
      patientId,
      'consultation',
      'information'
    ],
    {
      queryParams: {
        appointmentId,
        // Agregue el expertId porque en el toggle que hace petición al método show-or-hide requiere el id del experto en la url
        // Revisar si se puede cambiar para no mandarlo en la URL.
        // Esta el mismo problema en el componente summary-page en el método onGoToConsultation
        expert: this.userService.getExpertId(user),
      }
    });
  }
}
